import { Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { S3Client, PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { environment } from '../../environments/environment';
import imageCompression from 'browser-image-compression';
import { CognitoCredentialsService } from './cognito-credentials.service';

interface MediaInfo {
  mediaType: 'image' | 'video' | 'pdf';
  file: File | Blob;
  orientation: 'landscape' | 'portrait';
  preview?: string;  // For images and videos only
}

@Injectable({
  providedIn: 'root'
})
export class ImageUploadService {

  private s3ClientCache: Map<string, S3Client> = new Map();

  constructor(
    private storage: AngularFireStorage,
    private cognitoCredentials: CognitoCredentialsService
  ) {}

  private getBucketName(countryISO?: string): string {
    if (!environment.production) {
      // Development environment
      if (countryISO === 'ARE' || countryISO === 'TEST_ARE') {
        return environment.s3Buckets.developmentUae;
      }
      return environment.s3Buckets.development;
    }
    // Production environment
    if (!countryISO) {
      return environment.s3Buckets.productionEu;
    }
    return countryISO === 'ARE' || countryISO === 'TEST_ARE'
      ? environment.s3Buckets.productionUae
      : environment.s3Buckets.productionEu;
  }

  private getRegion(countryISO?: string): string {
    if (!countryISO) {
      return 'eu-central-1';
    }
    if (countryISO === 'ARE' || countryISO === 'TEST_ARE') {
      return 'me-central-1';
    }
    return 'eu-central-1';
  }

  /**
   * Get or create S3 client for a specific region with Cognito credentials
   */
  private async getS3Client(region: string): Promise<S3Client> {
    // Check cache first
    const cachedClient = this.s3ClientCache.get(region);
    if (cachedClient) {
      return cachedClient;
    }

    try {
      // Get credentials from Cognito using Firebase token
      const credentials = await this.cognitoCredentials.getCredentials(region);

      // Create S3 client with Cognito credentials
      const s3Client = new S3Client({
        region: region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken, // Required for temporary credentials
        },
      });

      // Cache the client
      this.s3ClientCache.set(region, s3Client);
      return s3Client;
    } catch (error) {
      console.error(`Error creating S3 client for region ${region}:`, error);
      throw error;
    }
  }

  async compressAndUploadFiles(
    mainFolder: string,
    businessPrefix: string,
    validFiles: File[],
    countryISO?: string
  ): Promise<{ mediaType: string, link: string, orientation: string }[]> {
    const processPromises: Promise<MediaInfo>[] = validFiles.map(file => {
      if (file.type.startsWith('image/')) {
        return this.compressFile(file);
      } else if (file.type.startsWith('video/')) {
        return this.getVideoInfo(file);
      } else {
        return Promise.resolve({
          mediaType: 'pdf',
          file: file,
          orientation: 'portrait',
        });
      }
    });

    const mediaInfoArray = await Promise.all(processPromises);

    const uploadPromises = mediaInfoArray.map(async (mediaInfo) => {
      let filePath = '';
      const randomName = Math.random().toString(36).substring(2, 10);

      if (mediaInfo.mediaType === 'image') {
        filePath = `${mainFolder}/${businessPrefix}/images/${randomName}`;
      } else if (mediaInfo.mediaType === 'video') {
        filePath = `${mainFolder}/${businessPrefix}/videos/${randomName}.mp4`;
      } else {
        filePath = `${mainFolder}/${businessPrefix}/files/${randomName}`;
      }

      // Pass countryISO to uploadToAws (now optional)
      const uploadedUrl = await this.uploadToAws(mediaInfo.file, filePath, countryISO);

      return {
        mediaType: mediaInfo.mediaType,
        link: uploadedUrl,
        orientation: mediaInfo.orientation,
      };
    });

    return await Promise.all(uploadPromises);
  }

  private uploadToFirebase(file: Blob | File, filePath: string): Promise<string> {
    const fileRef = this.storage.ref(filePath);
    const uploadTask = fileRef.put(file);

    return new Promise((resolve, reject) => {
      uploadTask
        .then(() => {
          fileRef.getDownloadURL().subscribe(
            (url) => resolve(url),
            (error) => reject(error)
          );
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * Convert File/Blob to Uint8Array for AWS SDK compatibility
   */
  private async fileToUint8Array(file: Blob | File): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(reader.result));
        } else {
          reject(new Error('Failed to convert file to Uint8Array'));
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  private async uploadToAws(
    file: Blob | File,
    filePath: string,
    countryISO?: string
  ): Promise<string> {
    const bucketName = this.getBucketName(countryISO);
    const contentType = file.type || 'application/octet-stream';
    const region = this.getRegion(countryISO);

    try {
      // Convert File/Blob to Uint8Array for AWS SDK compatibility
      const fileBuffer = await this.fileToUint8Array(file);

      // Get S3 client with Cognito credentials
      const s3Client = await this.getS3Client(region);

      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: filePath,
        Body: fileBuffer,
        ContentType: contentType,
        
        // ACL removed - bucket should be configured for public access via bucket policy
        // If you need ACL control, add s3:PutObjectAcl permission to IAM role
      });
      
      await s3Client.send(command);
      
      // Use the correct regional endpoint for the URL
      return region === 'me-central-1'
        ? `https://${bucketName}.s3.me-central-1.amazonaws.com/${filePath}`
        : `https://${bucketName}.s3.eu-central-1.amazonaws.com/${filePath}`;
    } catch (error: any) {
      console.error('Error uploading to AWS:', error);
      
      // Handle token expiration - try to refresh
      if (error.name === 'NotAuthorizedException' || error.message?.includes('token') || error.message?.includes('expired')) {
        try {
          // Clear cache and retry
          this.cognitoCredentials.clearCacheForRegion(region);
          this.s3ClientCache.delete(region);
          
          // Convert file to Uint8Array again for retry
          const fileBuffer = await this.fileToUint8Array(file);
          
          const s3Client = await this.getS3Client(region);
          const command = new PutObjectCommand({
            Bucket: bucketName,
            Key: filePath,
            Body: fileBuffer,
            ContentType: contentType,
            // ACL removed - bucket should be configured for public access via bucket policy
          });
          await s3Client.send(command);
          
          return region === 'me-central-1'
            ? `https://${bucketName}.s3.me-central-1.amazonaws.com/${filePath}`
            : `https://${bucketName}.s3.eu-central-1.amazonaws.com/${filePath}`;
        } catch (retryError) {
          throw new Error('Authentication failed. Please log in again.');
        }
      }
      
      throw error;
    }
  }

  async deleteFromAws(filePathOrUrl: string, countryISO?: string): Promise<void> {
    const bucketName = this.getBucketName(countryISO);
    const region = this.getRegion(countryISO);

    // Extract file path if a full URL is provided
    let fileKey = filePathOrUrl;
    if (fileKey.startsWith('http')) {
      try {
        const url = new URL(fileKey);
        // Remove leading slash
        fileKey = url.pathname.substring(1);
      } catch (e) {
        throw new Error('Invalid S3 URL provided for deletion.');
      }
    }

    try {
      // Get S3 client with Cognito credentials
      const s3Client = await this.getS3Client(region);

      const command = new DeleteObjectCommand({
        Bucket: bucketName,
        Key: fileKey,
      });
      
      await s3Client.send(command);
      // console.log(`✅ Deleted S3 object: ${fileKey}`);
    } catch (error: any) {
      console.error('❌ Error deleting from AWS S3:', error);
      
      // Handle token expiration - try to refresh
      if (error.name === 'NotAuthorizedException' || error.message?.includes('token') || error.message?.includes('expired')) {
        try {
          this.cognitoCredentials.clearCacheForRegion(region);
          this.s3ClientCache.delete(region);
          
          const s3Client = await this.getS3Client(region);
          const command = new DeleteObjectCommand({
            Bucket: bucketName,
            Key: fileKey,
          });
          await s3Client.send(command);
          return;
        } catch (retryError) {
          throw new Error('Authentication failed. Please log in again.');
        }
      }
      
      throw error;
    }
  }

  // Handle video files
  private async getVideoInfo(file: File): Promise<MediaInfo> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const reader = new FileReader();

      reader.onload = () => {
        video.src = reader.result as string;
        video.onloadedmetadata = () => {
          const isLandscape = video.videoWidth > video.videoHeight;
          resolve({
            mediaType: 'video',
            file: file,  // We return the original file for upload
            orientation: isLandscape ? 'landscape' : 'portrait',
            preview: reader.result as string  // Video preview if needed
          });
        };
        video.onerror = reject;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);  // Read the video as a data URL
    });
  }

  private async compressFile(file: File): Promise<MediaInfo> {
    
    // console.log(`Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    
    const options = {
      maxSizeMB: 0.3, // Target 300KB
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg', // Force JPEG for better compression
      initialQuality: 0.8, // Start with 80% quality
      alwaysKeepResolution: false
    };
  
    try {
      const compressedFile = await imageCompression(file, options);
      // console.log(`Compressed file size: ${(compressedFile.size / 1024).toFixed(2)} KB`);
      
      // Get image dimensions for orientation
      const img = await this.loadImageFromFile(compressedFile);
      const isLandscape = img.width > img.height;
      
      return {
        mediaType: 'image',
        file: compressedFile,
        orientation: isLandscape ? 'landscape' : 'portrait',
        preview: await this.fileToDataURL(compressedFile)
      };
    } catch (error) {
      console.error('Compression failed:', error);
      // Fallback to original file if compression fails
      const img = await this.loadImageFromFile(file);
      const isLandscape = img.width > img.height;
      
      return {
        mediaType: 'image',
        file: file,
        orientation: isLandscape ? 'landscape' : 'portrait',
        preview: await this.fileToDataURL(file)
      };
    }
  }

  // Helper to load image from File object
private async loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

  // Helper to convert File to DataURL
private async fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
}



